import { useEffect, useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import SignatureCanvas from '@/components/SignatureCanvas';
import { getPublicQuoteStrings, type PublicQuoteLocale } from '@/lib/publicQuoteLocale';

interface SignRecipientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientName: string;
  locale: PublicQuoteLocale;
  submitting: boolean;
  onSubmit: (signatureDataUrl: string) => void;
}

/** Ondertekenen door één tekenbevoegde wanneer de offerte meerdere tekenbevoegden heeft. */
function SignRecipientDialog({
  open,
  onOpenChange,
  recipientName,
  locale,
  submitting,
  onSubmit,
}: SignRecipientDialogProps) {
  const copy = getPublicQuoteStrings(locale).signature;
  const [signature, setSignature] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setSignature(null);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => (submitting ? undefined : onOpenChange(next))}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{copy.signDialogTitle}</DialogTitle>
          <DialogDescription>
            {copy.signDialogIntro.replace('{name}', recipientName || copy.clientSignature)}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex">
          <SignatureCanvas locale={locale} onSignatureChange={setSignature} disabled={submitting} />
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {copy.signDialogCancel}
          </Button>
          <Button
            className="gap-2 bg-green-600 hover:bg-green-700"
            onClick={() => signature && onSubmit(signature)}
            disabled={!signature || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {copy.acceptLoading}
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                {copy.signDialogSubmit}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SignRecipientDialog;
